/**
 * Where a song's cover art gets its visual direction from.
 *
 * WHY THIS EXISTS. Every generated song used to draw a gradient seeded on its own id
 * (lib/artwork.artworkSeedFor's fallback), which means the cover was decided by a UUID:
 * two songs from the same prompt got different covers, and a song about a neon skyline
 * got the same kind of picture as a song about a harvest festival. That is a static
 * placeholder wearing the clothes of generated artwork, and the batch that added this
 * module asked specifically that it not be one.
 *
 * So the direction is DERIVED, from the three things the request already carries, in the
 * order the brief names them:
 *
 *   1. GENRE  → the style. A committed genre→style map over the app's own COVER_STYLES
 *               vocabulary (data/music.ts), so a Synthwave track is "Neon" and a string
 *               quartet is "Minimal". Keyword-matched rather than exact, because three
 *               different screens generate with three different genre vocabularies
 *               (GENRES, GM_GENRES, DNA_GENRES) and "Cinematic pop" must land with
 *               "Cinematic".
 *   2. MOOD   → the treatment. Light, palette and atmosphere words, not subject matter.
 *   3. PROMPT → the subject: `strongestVisualConcept` below, which is the part of the
 *               brief that says "the strongest / most visually striking concept in the
 *               user's prompt".
 *
 * WHAT IS REAL HERE, AND WHAT IS NOT. All of the above is real: it is a deterministic
 * reading of the request, it is unit tested, and its output is the `imagePrompt` an image
 * service would be sent — that string is the boundary. What does NOT happen is the
 * generation: there is no image model behind this app, so `coverUrl` stays null and the
 * `seed` below feeds the gradient stand-in exactly as it did before. The difference is
 * that the gradient is now a function of the prompt, the genre and the mood rather than
 * of a UUID. See api/coverApi.describeCoverBlockers() for the machine-readable list of
 * what is missing, which the Studio's panel renders rather than restates.
 *
 * NO NEW COVER-ART STATE. This module returns a seed and a style, which are two fields
 * `Song` has had since the prototype (`artworkSeed`, `coverStyle`). Nothing here is
 * stored anywhere else, and every surface keeps resolving artwork through
 * lib/artwork.artworkSeedFor.
 *
 * Pure and deterministic, like lib/music: same input, same output. The one place a caller
 * may inject variation is `variant`, and its own comment says why.
 */
import { COVER_STYLES } from '../data/music';
import { hashString } from './hash';

/**
 * The visual direction for one song's cover.
 *
 * A record rather than a bare seed string, because the seed is the only part this build
 * can act on and the rest is the part a real image service needs — keeping them together
 * is what makes the boundary inspectable instead of implied.
 */
export interface CoverConcept {
  /**
   * The strongest visual concept in the prompt, or null when the prompt had none.
   *
   * Null is a real outcome and not a failure: "make me something for the gym" describes
   * a use, not a picture. The direction is then carried by genre and mood alone, which
   * is honest, rather than by a noun invented to fill the field.
   */
  subject: string | null;
  /** Style, from COVER_STYLES. Chosen by genre unless the caller named one. */
  style: string;
  /** The genre the style came from, echoed so a caller need not re-derive it. */
  genre: string | null;
  /** The mood the treatment came from. */
  mood: string | null;
  /**
   * The instruction an image model would be sent.
   *
   * THE ACTUAL BOUNDARY. This is the half of cover generation that is finished: the
   * request is composed from the song's own genre, mood and prompt, and wiring a real
   * service is a change to coverApi.generateCover's body rather than to any caller. It
   * is also what makes "does this use the prompt?" a question with a testable answer.
   */
  imagePrompt: string;
  /**
   * Seed for the gradient stand-in — the one value this build can actually render.
   *
   * Derived from the style, the genre, the mood and the subject, so two songs with the
   * same direction look the same and two with different directions do not. NOT derived
   * from the song id, which is the defect this module was written to end.
   */
  seed: string;
}

/**
 * Genre keyword → cover style.
 *
 * Ordered, and matched by substring against the lowercased genre, because the first
 * match wins and some genres contain two keywords: "Cinematic pop" is cinematic before
 * it is pop, and "Country Pop" is country before it is pop. An exact-match table would
 * have to list all thirty-odd genres from three vocabularies and would still miss the
 * next one someone adds.
 *
 * Every value is a member of COVER_STYLES — asserted in the tests, so a typo here cannot
 * put a style on a song that the Studio's own picker does not offer.
 *
 * "Anime" is deliberately absent. No genre in the app implies it, and mapping one to it
 * arbitrarily would be this file inventing a house style; it stays available as a manual
 * choice in the Cover Art panel.
 */
export const GENRE_COVER_STYLES: ReadonlyArray<readonly [string, string]> = [
  ['cinematic', 'Cinematic'],
  ['classical', 'Minimal'],
  ['ambient', 'Minimal'],
  ['synthwave', 'Neon'],
  ['synth', 'Neon'],
  ['edm', 'Neon'],
  ['house', 'Neon'],
  ['techno', 'Neon'],
  ['amapiano', 'Neon'],
  ['dance', 'Neon'],
  ['lo-fi', 'Retro'],
  ['lofi', 'Retro'],
  ['jazz', 'Retro'],
  ['blues', 'Retro'],
  ['soul', 'Retro'],
  ['acoustic', 'Realistic'],
  ['folk', 'Realistic'],
  ['country', 'Realistic'],
  ['gospel', 'Realistic'],
  ['indie', 'Realistic'],
  ['hip hop', 'Modern'],
  ['rap', 'Modern'],
  ['trap', 'Modern'],
  ['r&b', 'Modern'],
  ['afrobeat', 'Modern'],
  ['afrobeats', 'Modern'],
  ['dancehall', 'Modern'],
  ['reggae', 'Modern'],
  ['pop', 'Modern'],
  ['rock', 'Modern'],
];

/**
 * The style a song gets when its genre matches nothing above, or when there is no genre.
 *
 * "Modern" rather than the first entry of COVER_STYLES, because a default is a design
 * decision and `COVER_STYLES[0]` would silently become a different one the day that list
 * is reordered.
 */
export const DEFAULT_COVER_STYLE = 'Modern';

/**
 * Mood → how the picture is lit and coloured.
 *
 * TREATMENT ONLY, never subject matter. A mood tells you whether a room is warm or cold;
 * it does not tell you there is a room. Keeping the two apart is what lets the prompt own
 * the subject — a "Melancholic" song about a carnival should be a carnival, dimly lit,
 * and not a rainy window.
 *
 * Covers DNA_MOODS (what generation stamps on a song) and GM_MOODS (what the Genre Mixer
 * offers). Keyed lowercase; an unlisted mood falls through to the mood word itself, which
 * is a weaker instruction but a true one.
 */
export const MOOD_COVER_TREATMENTS: Readonly<Record<string, string>> = {
  // DNA_MOODS
  euphoric: 'bright, saturated colour and blown-out highlights',
  melancholic: 'muted, desaturated colour and soft overcast light',
  nostalgic: 'warm faded colour, as though the image had aged',
  triumphant: 'high contrast and strong directional light',
  dreamy: 'soft focus, pastel colour and diffuse light',
  restless: 'cold colour, hard edges and motion in the frame',
  tender: 'gentle warm light and low contrast',
  defiant: 'stark contrast and deep shadow',
  // GM_MOODS not already covered above
  happy: 'clear warm light and open, cheerful colour',
  emotional: 'low, intimate light and restrained colour',
  dark: 'near-black shadow and a narrow, cold palette',
  romantic: 'warm low light and shallow depth of field',
  energetic: 'vivid colour and a sense of movement',
  epic: 'wide scale, dramatic light and deep shadow',
  calm: 'even, quiet light and a limited palette',
  hopeful: 'soft rising light and gentle warmth',
  aggressive: 'harsh light, heavy contrast and a hot palette',
};

/**
 * Words that carry a picture, and how strongly.
 *
 * THIS IS THE VOCABULARY, AND IT IS COMMITTED ON PURPOSE. Deciding which part of a
 * sentence is the most visually striking is a judgement, and a judgement made by a table
 * you can read and argue with is worth more than one made by an opaque heuristic. It is
 * also the honest scope of what a browser can do here: this is a lexical reading of the
 * prompt, not a language model, and it does not pretend otherwise.
 *
 *   3 — a subject you could photograph. Places, objects, weather, celestial things.
 *   2 — a quality of light, colour or surface. Modifies a subject and can lead a phrase.
 *   1 — time and season. Real visual information, but weak on its own.
 *
 * Everything else scores 0, which is what makes phrase-building work below: articles,
 * verbs and abstractions are the gaps between concepts.
 */
export const VISUAL_TERMS: Readonly<Record<string, number>> = {
  // ---- 3: subjects ----
  skyline: 3,
  city: 3,
  cityscape: 3,
  street: 3,
  streets: 3,
  streetlights: 3,
  alley: 3,
  rooftop: 3,
  rooftops: 3,
  window: 3,
  windows: 3,
  mirror: 3,
  glass: 3,
  highway: 3,
  road: 3,
  bridge: 3,
  train: 3,
  car: 3,
  ocean: 3,
  sea: 3,
  waves: 3,
  river: 3,
  lake: 3,
  beach: 3,
  shore: 3,
  desert: 3,
  dunes: 3,
  forest: 3,
  woods: 3,
  trees: 3,
  mountain: 3,
  mountains: 3,
  valley: 3,
  field: 3,
  fields: 3,
  garden: 3,
  rain: 3,
  storm: 3,
  thunder: 3,
  lightning: 3,
  snow: 3,
  fog: 3,
  mist: 3,
  clouds: 3,
  sky: 3,
  sun: 3,
  sunset: 3,
  sunrise: 3,
  moon: 3,
  moonlight: 3,
  stars: 3,
  starlight: 3,
  fire: 3,
  flames: 3,
  smoke: 3,
  dust: 3,
  candle: 3,
  candles: 3,
  lantern: 3,
  lanterns: 3,
  church: 3,
  cathedral: 3,
  temple: 3,
  market: 3,
  carnival: 3,
  festival: 3,
  stage: 3,
  crowd: 3,
  dancefloor: 3,
  bedroom: 3,
  kitchen: 3,
  cafe: 3,
  bar: 3,
  studio: 3,
  vinyl: 3,
  tape: 3,
  polaroid: 3,
  neon: 3,
  // ---- 2: light, colour, surface ----
  golden: 2,
  amber: 2,
  crimson: 2,
  scarlet: 2,
  violet: 2,
  indigo: 2,
  turquoise: 2,
  emerald: 2,
  silver: 2,
  gold: 2,
  copper: 2,
  monochrome: 2,
  pastel: 2,
  bright: 2,
  dark: 2,
  darkened: 2,
  pale: 2,
  faded: 2,
  glowing: 2,
  glow: 2,
  shimmering: 2,
  gleaming: 2,
  burning: 2,
  frozen: 2,
  wet: 2,
  damp: 2,
  dusty: 2,
  hazy: 2,
  misty: 2,
  foggy: 2,
  smoky: 2,
  velvet: 2,
  marble: 2,
  chrome: 2,
  rusted: 2,
  shattered: 2,
  cracked: 2,
  lit: 2,
  backlit: 2,
  silhouetted: 2,
  reflected: 2,
  reflections: 2,
  shadows: 2,
  shadow: 2,
  // ---- 1: time and season ----
  dawn: 1,
  morning: 1,
  noon: 1,
  afternoon: 1,
  dusk: 1,
  twilight: 1,
  evening: 1,
  night: 1,
  midnight: 1,
  summer: 1,
  winter: 1,
  autumn: 1,
  spring: 1,
};

/** Most words a derived subject may run to, so a whole sentence cannot become one. */
const MAX_SUBJECT_WORDS = 4;

/** A word of the prompt, with its score and whether punctuation precedes it. */
interface ScoredWord {
  /** As the user typed it, so the returned phrase reads like their own words. */
  text: string;
  score: number;
  /**
   * True when punctuation — not merely a space — separates this word from the last.
   *
   * Phrase growth stops here, because a comma or a semicolon is a boundary between
   * concepts and not a gap inside one. Without it "rain; smoke" reads as the single
   * subject "rain smoke", which is two pictures welded into a phrase describing
   * neither. Words scoring 0 are the other boundary; this is the one punctuation makes.
   */
  breaksBefore: boolean;
}

/**
 * Splits a prompt into scored words, keeping the user's own casing.
 *
 * Apostrophes and hyphens survive inside a word ("city's", "lo-fi"); everything else
 * separates. The lookup lowercases, so "Neon" and "neon" score alike, but what is
 * RETURNED is the original — a subject that came back lowercased would look like a bug
 * in a caption.
 *
 * Matched rather than split so the text BETWEEN words survives long enough to be
 * classified: a plain space joins a phrase, punctuation ends one.
 */
function scoreWords(prompt: string): ScoredWord[] {
  const words: ScoredWord[] = [];
  let cursor = 0;

  for (const match of prompt.matchAll(/[\p{L}\p{N}'’-]+/gu)) {
    const gap = prompt.slice(cursor, match.index);
    cursor = match.index + match[0].length;
    // Trailing possessives and stray hyphens are stripped for the lookup only.
    const key = match[0].toLowerCase().replace(/['’]s$/u, '').replace(/^-+|-+$/gu, '');
    words.push({
      text: match[0],
      score: VISUAL_TERMS[key] ?? 0,
      breaksBefore: /\S/u.test(gap),
    });
  }

  return words;
}

/**
 * The most visually striking concept in a prompt, as a phrase, or null when there is none.
 *
 * HOW IT DECIDES, because "strongest" has to mean something checkable:
 *
 *   1. Score every word against VISUAL_TERMS.
 *   2. Take the highest-scoring word. Ties go to the EARLIEST, which is deliberate —
 *      the head of a sentence is what it is about, and a later tie is usually an aside.
 *   3. Grow outwards through adjacent scoring words, up to MAX_SUBJECT_WORDS, so
 *      "a neon lit skyline at night" yields "neon lit skyline" rather than "skyline".
 *      Words scoring 0 — articles, verbs, abstractions — are the boundaries, which is
 *      why they are not in the table.
 *
 * Returns null when nothing scores, and the caller must handle that rather than
 * substituting a noun of its own. See CoverConcept.subject.
 */
export function strongestVisualConcept(prompt: string): string | null {
  const words = scoreWords(prompt);

  let peak = -1;
  for (let i = 0; i < words.length; i++) {
    if (peak === -1 || words[i].score > words[peak].score) peak = i;
  }
  if (peak === -1 || words[peak].score === 0) return null;

  let from = peak;
  let to = peak;
  /*
    Grown outwards rather than by scanning every window, and preferring whichever
    neighbour scores higher, so a phrase extends toward the picture and not merely
    leftwards. Three things stop it: a zero-scoring word, punctuation, or the cap.
    A neighbour across a comma is treated as absent — see ScoredWord.breaksBefore.
  */
  while (to - from + 1 < MAX_SUBJECT_WORDS) {
    const before = from > 0 && !words[from].breaksBefore ? words[from - 1].score : 0;
    const after = to < words.length - 1 && !words[to + 1].breaksBefore ? words[to + 1].score : 0;
    if (before === 0 && after === 0) break;
    if (before >= after) from -= 1;
    else to += 1;
  }

  return words
    .slice(from, to + 1)
    .map((word) => word.text)
    .join(' ');
}

/** The cover style a genre implies. Exported so a caller can ask without a full concept. */
export function coverStyleForGenre(genre: string | undefined | null): string {
  if (!genre) return DEFAULT_COVER_STYLE;
  const needle = genre.toLowerCase();
  for (const [keyword, style] of GENRE_COVER_STYLES) {
    if (needle.includes(keyword)) return style;
  }
  return DEFAULT_COVER_STYLE;
}

/** How a mood should be lit. Falls through to the mood itself when unlisted. */
export function coverTreatmentForMood(mood: string | undefined | null): string | null {
  if (!mood) return null;
  return MOOD_COVER_TREATMENTS[mood.toLowerCase()] ?? `a ${mood.toLowerCase()} atmosphere`;
}

export interface CoverConceptInput {
  /** The song's own prompt — where the subject comes from. */
  prompt?: string | null;
  /** The song's genre — where the style comes from. */
  genre?: string | null;
  /** The song's mood — where the treatment comes from. */
  mood?: string | null;
  /**
   * A style the user picked, overriding the one the genre implies.
   *
   * The Studio's Cover Art panel is the only caller that passes it: a person looking at
   * their own song is a better judge of its cover than a keyword table, and the panel
   * exists to let them say so. Ignored when it is not a real style.
   */
  style?: string | null;
  /**
   * Extra text folded into the seed, to get a different rendering of the same direction.
   *
   * FOR THE MANUAL PATH ONLY, and the asymmetry is the point. Automatic artwork must be
   * reproducible from the request — that is what makes "this cover came from this prompt"
   * a checkable claim — so `createGeneration` passes nothing here and the same prompt,
   * genre and mood always give the same cover. A person pressing "Regenerate" is asking
   * for a different take on artwork they have already seen, so the Studio passes a fresh
   * value; a real image model behaves the same way (its output is documented as
   * non-deterministic between calls — see api/lyria.ts).
   */
  variant?: string | number | null;
}

/**
 * Builds the visual direction for a cover, from the request that made the song.
 *
 * The three inputs are independent: a missing prompt still gives a genre-and-mood
 * direction, a missing genre still gives a subject, and a song with none of the three
 * gets the default style and a seed derived from that alone. No input is invented to
 * fill a gap.
 */
export function coverConceptFor(input: CoverConceptInput): CoverConcept {
  const genre = input.genre?.trim() || null;
  const mood = input.mood?.trim() || null;
  const userDirection = input.prompt?.trim() || null;
  const subject = strongestVisualConcept(userDirection ?? '');

  const named = input.style?.trim();
  const style = named && COVER_STYLES.includes(named) ? named : coverStyleForGenre(genre);

  const treatment = coverTreatmentForMood(mood);

  /*
    The instruction, assembled in the brief's own order: subject, then style, then
    treatment. Plain sentences rather than pseudo-fields, for the reason
    lyria.buildImageMusicPrompt gives — an image service reads a prompt, not a schema,
    and `genre: Synthwave` in a prompt is a string that happens to contain a colon.
  */
  const parts: string[] = [
    userDirection
      ? `Cover art for a piece of music.${subject ? ` Subject: ${subject}.` : ''} User direction: ${userDirection}`
      : 'Cover art for a piece of music.',
    `Style: ${style.toLowerCase()}.`,
  ];
  if (genre) parts.push(`The music is ${genre}.`);
  if (treatment) parts.push(`Light and colour: ${treatment}.`);
  parts.push('Square format. No text, lettering or logos in the image.');
  const imagePrompt = parts.join(' ');

  /*
    THE SEED, and what it is made of is the whole answer to "is this derived or static?".

    Built from the direction and hashed, rather than from the song id: `artworkSeedFor`
    falls back to the id, and that fallback is what made a generated song's cover a
    function of a UUID. Prefixed `cover-` so a seed is recognisable in a store dump, and
    joined with a separator that cannot appear in a hash so two different directions
    cannot collide by concatenation.
  */
  const seed = [
    'cover',
    hashString([style, genre ?? '', mood ?? '', subject ?? '', String(input.variant ?? '')].join('|')),
  ].join('-');

  return { subject, style, genre, mood, imagePrompt, seed };
}
